import { TFunction } from 'i18next';
import { MAX_CUIL, MIN_CUIL } from '../../../constants/values';

export type ValidationName =
    | 'required'
    | 'minLength'
    | 'maxLength'
    | 'fixedLength'
    | 'lettersOnly'
    | 'cuil'
    | 'cuitModulo11'
    | 'maxValue'
    | 'minValue'
    | 'phoneNumber'
    | 'email'
    | 'cpa';

/**
 * Dígito verificador oficial de AFIP (Módulo 11) para CUIT/CUIL de 11 dígitos.
 * `cuil` (arriba) solo valida un rango numérico — esto valida el checksum real,
 * necesario porque la SRT rechaza CUIT con formato válido pero checksum inválido.
 */
const esCuitValidoPorModulo11 = (value: string): boolean => {
    const digitos = value.replace(/\D/g, '');
    if (digitos.length !== 11) return false;

    const multiplicadores = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
    const suma = multiplicadores.reduce(
        (acc, multiplicador, indice) => acc + multiplicador * Number(digitos[indice]),
        0
    );
    const resto = suma % 11;
    const digitoVerificador = resto === 0 ? 0 : resto === 1 ? 9 : 11 - resto;

    return digitoVerificador === Number(digitos[10]);
};

export type ValidationRule = {
    name: ValidationName;
    param?: number;
    errorMessage?: string;
};

export const validationRules = (
    t: TFunction
): Record<ValidationName, (value: any, param?: number, errorMessage?: string) => string> => ({
    required: (value: any) => {
        const isEmptyString = typeof value === 'string' && value.trim() === '';

        return value === null || value === undefined || isEmptyString
            ? t('validation.required')
            : '';
    },
    minLength: (value: any, param?: number) => {
        return param && value && value.length < param ? t('validation.minLength', { param }) : '';
    },
    maxLength: (value: any, param?: number) => {
        return param && value && value.length > param ? t('validation.maxLength', { param }) : '';
    },
    lettersOnly: (value: any) => {
        // Nombres y apellidos en castellano: tildes, ñ y diéresis son letras
        // ("Gómez", "Muñoz", "Güemes"). La base (latin1) y el backend las aceptan.
        const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]*$/;
        return !regex.test(value) ? t('validation.lettersOnly') : '';
    },
    fixedLength: (value: any, param?: number) => {
        return param && value && value.length !== param
            ? t('validation.fixedLength', { param })
            : '';
    },
    maxValue: (value: any, param?: number) => {
        const numberValue = Number(value);
        if (isNaN(numberValue)) return t('validation.invalidNumber');
        return param !== undefined && numberValue > param
            ? t('validation.maxValue', { param })
            : '';
    },
    minValue: (value: any, param?: number) => {
        const numberValue = Number(value);
        if (isNaN(numberValue)) return t('validation.invalidNumber');
        return param !== undefined && numberValue < param
            ? t('validation.minValue', { param })
            : '';
    },
    cuil: (value: any) => {
        const cuilNumber = Number(value);

        if (cuilNumber < MIN_CUIL || cuilNumber > MAX_CUIL) {
            return t('validation.invalidCUIL');
        }
        return '';
    },
    cuitModulo11: (value: any) => {
        if (value === null || value === undefined || value === '') return '';

        return esCuitValidoPorModulo11(value.toString())
            ? ''
            : t('validation.invalidCUITModulo11');
    },
    phoneNumber: (value: any, param?: number) => {
        if (value === null || value === undefined || value === '') return '';

        if (!/^\d+$/.test(value.toString())) return t('validation.invalidPhoneNumber');

        const minLength = param ?? 10;
        const length = value.toString().length;
        if (length < minLength) return t('validation.phoneNumberTooShort', { param: minLength });
        if (length > 15) return t('validation.phoneNumberTooLong');

        return '';
    },
    email: (value: any) => {
        if (value === null || value === undefined || value === '') return '';

        const emailPattern = /\S+@\S+\.\S+/;
        if (!emailPattern.test(value)) return t('validation.invalidEmail');
        return '';
    },
    cpa: (value: any) => {
        if (value === null || value === undefined || value === '') return '';

        const cpaPattern = /^[A-Za-z]\d{4}[A-Za-z]{3}$/;
        if (!cpaPattern.test(value)) return t('validation.invalidCPA');
        return '';
    }
});

const useValidation = (t: TFunction) => {
    const validateRules = validationRules(t);

    const validate = (value: any, validations: ValidationRule[]) => {
        let hasError = false;
        let errorText = '';

        for (const validation of validations) {
            const validationFn = validateRules[validation.name];
            if (validationFn) {
                const error = validationFn(value, validation.param);
                if (error) {
                    hasError = true;
                    errorText = error;
                    break;
                }
            }
        }

        return { hasError, errorText };
    };

    return { validate };
};

export default useValidation;
